import { cn } from "@/utils";
import React from "react";

/**
 * Shared row primitive for the Canvas Explorer scopes. Spacing /
 * typography / hover behaviour are lifted from `MenuRow` in
 * `AccountPopover` so chat rows, file rows, folder rows, and workspace
 * tiles all read as one family.
 *
 * Stays as a pure presentational shell — drag/drop wiring lives on the
 * scope-specific row wrappers, which forward `draggable` / drag handlers
 * through to the underlying `<button>` via `htmlProps`.
 */
export interface ExplorerRowProps {
  leading?: React.ReactNode;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  trailing?: React.ReactNode;
  selected?: boolean;
  /**
   * When false, skip `data-rail-selected` — the parent wrapper is the
   * contiguous-outline host (e.g. ExplorerFileRow tooltip trigger).
   */
  selectedChrome?: boolean;
  /** Drag-over highlight (target) or invalid (same-scope) variant. */
  dropState?: "idle" | "over" | "invalid";
  active?: boolean;
  onClick?: (e: React.MouseEvent) => void;
  onDoubleClick?: (e: React.MouseEvent) => void;
  onContextMenu?: (e: React.MouseEvent) => void;
  htmlProps?: React.HTMLAttributes<HTMLDivElement> & {
    draggable?: boolean;
    onDragStart?: (e: React.DragEvent) => void;
    onDragEnter?: (e: React.DragEvent) => void;
    onDragOver?: (e: React.DragEvent) => void;
    onDragLeave?: (e: React.DragEvent) => void;
    onDrop?: (e: React.DragEvent) => void;
  };
  className?: string;
}

export const ExplorerRow = React.forwardRef<HTMLDivElement, ExplorerRowProps>(
  function ExplorerRow(
    {
      leading,
      title,
      subtitle,
      trailing,
      selected,
      selectedChrome = true,
      active,
      dropState = "idle",
      onClick,
      onDoubleClick,
      onContextMenu,
      htmlProps,
      className,
    },
    ref,
  ) {
    return (
      <div
        ref={ref}
        role="button"
        tabIndex={0}
        // Marker the explorer's surface-rail click handler uses to
        // distinguish "click on a row" (do nothing) from "click on
        // empty space" (clear selection).
        data-explorer-row=""
        onClick={onClick}
        onDoubleClick={onDoubleClick}
        onContextMenu={onContextMenu}
        className={cn(
          "group flex items-center gap-2.5 min-h-7 px-2",
          "text-[13px] font-medium select-none cursor-pointer",
          // Default: foreground/85 with subtle hover background, mirroring
          // AccountPopover's MenuRow.
          "text-foreground/85 hover:bg-n-05",
          !selected && "rounded-md",
          // Selected fill; contiguous outline comes from data-rail-selected CSS.
          selected && "bg-foreground/[0.04] text-foreground",
          // Active page (e.g. current canvas tile in workspace scope) is
          // a stronger emphasis than selection — shouldn't co-occur, but
          // selected wins if both are true.
          active && !selected && "bg-n-05 text-n-90",
          // Drop-target hover state.
          dropState === "over" &&
            "bg-primary/8 ring-1 ring-rust/40 dark:bg-primary/15",
          dropState === "invalid" && "ring-1 ring-rust/40 opacity-70",
          className,
        )}
        {...htmlProps}
        {...(selected && selectedChrome
          ? {
              "data-rail-list-item": "",
              "data-rail-chrome": "",
              "data-rail-selected": "",
            }
          : selectedChrome
            ? { "data-rail-list-item": "", "data-rail-chrome": "" }
            : {})}
      >
        {leading && (
          <span className="flex-shrink-0 flex items-center justify-center">
            {leading}
          </span>
        )}
        <div className="flex-1 min-w-0">
          <div className="truncate text-[13px] leading-tight">{title}</div>
          {subtitle && (
            <div className="truncate text-[11px] text-muted-foreground/80 leading-tight mt-0.5">
              {subtitle}
            </div>
          )}
        </div>
        {trailing && (
          <span className="flex-shrink-0 flex items-center text-muted-foreground/70">
            {trailing}
          </span>
        )}
      </div>
    );
  },
);
